/**
 * Link a device — the web entry screen.
 *
 * Built from docs/references/03-web-login.png and 04-web-login-expired.png.
 *
 * The important thing those references teach is not the layout, it is the
 * model: on the web, ZIvapp is a companion to the phone rather than a client
 * you sign up on. The phone holds the account; the browser borrows it. That
 * is why this screen leads with a QR code and keeps account creation behind
 * a single "Get started" link.
 *
 * The pairing token below is a placeholder. A real one has to be minted by
 * the server, bound to a session and consumed once — see the note on
 * requestPairingCode(). Until that endpoint exists the code encodes a URL
 * that resolves to the download page, so scanning it does something sensible
 * rather than nothing.
 *
 * Signing in by phone number is the path that actually works today, which is
 * why it is given real prominence rather than hidden.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { signedOutLanding } from '../App.jsx'
import QRCode from 'qrcode'
import ThemeToggle from '../components/ui/ThemeToggle.jsx'
import InstallBanner from '../components/pwa/InstallBanner.jsx'
import './LinkDevice.css'

/** How long a code stays valid. Short, because it grants account access. */
const QR_TTL_MS = 60_000

/**
 * Stands in for the server call that mints a pairing token.
 *
 * The real one belongs on the server as POST /api/auth/pairing: it should
 * return a single-use token bound to a fresh session, expiring in about a
 * minute, so a screenshot of this page is worthless a moment later.
 */
async function requestPairingCode () {
  const token = crypto.randomUUID()
  return `https://zivapp.com/link/${token}`
}

export default function LinkDevice () {
  const canvasRef = useRef(null)
  const [state, setState] = useState('loading') // loading | ready | expired

  const draw = useCallback(async () => {
    setState('loading')
    const payload = await requestPairingCode()
    const canvas = canvasRef.current
    if (!canvas) return

    await QRCode.toCanvas(canvas, payload, {
      width: 264,
      margin: 1,
      errorCorrectionLevel: 'H', // tolerates the mark punched into the middle
      color: { dark: '#0b2d1c', light: '#ffffff' }
    })
    setState('ready')
  }, [])

  useEffect(() => {
    draw()
    const timer = setTimeout(() => setState('expired'), QR_TTL_MS)
    return () => clearTimeout(timer)
  }, [draw])

  // Re-arm the expiry whenever a fresh code is drawn.
  useEffect(() => {
    if (state !== 'ready') return undefined
    const timer = setTimeout(() => setState('expired'), QR_TTL_MS)
    return () => clearTimeout(timer)
  }, [state])

  // Reached directly on a phone (a bookmark, a shared link): there is
  // nothing here a phone can use - it would be asking you to scan the code
  // with the device showing it - so hand over to onboarding. After the
  // hooks above, never before them, or the hook order changes on rotate.
  if (signedOutLanding() === '/onboarding') {
    return <Navigate to="/onboarding" replace />
  }

  return (
    <main className="link">
      <header className="link__bar">
        <span className="link__logo" aria-hidden="true">
          <svg viewBox="0 0 48 48" fill="none">
            <path
              d="M10 6h28a6 6 0 0 1 6 6v20a6 6 0 0 1-6 6H22l-11 8v-8h-1a6 6 0 0 1-6-6V12a6 6 0 0 1 6-6Z"
              stroke="currentColor" strokeWidth="3" strokeLinejoin="round"
            />
            <path d="M18 17h13l-13 12h13" stroke="currentColor" strokeWidth="3"
              strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
        <span className="link__wordmark">ZIvapp</span>
        <ThemeToggle className="link__theme" />
      </header>

      <div className="link__stack">
        {/* The install that actually exists. There is no Windows build to
            download, and the button that claimed otherwise pointed at a
            route with nothing behind it. */}
        <InstallBanner />

        {/* ---- the pairing card ---- */}
        <section className="link__card link__pair">
          <div className="link__steps">
            <h1 className="link__title">Scan to log in</h1>

            <ol className="link__list">
              <li><span className="link__num">1</span> Scan the QR code with your phone’s camera</li>
              <li>
                <span className="link__num">2</span> Tap the link to open <strong>ZIvapp</strong>
                <span className="link__chip" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none">
                    <path d="M5 3h14a3 3 0 0 1 3 3v10a3 3 0 0 1-3 3h-8l-5.5 4V19H5a3 3 0 0 1-3-3V6a3 3 0 0 1 3-3Z"
                      stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                    <path d="M9 8.5h6l-6 6h6" stroke="currentColor" strokeWidth="2"
                      strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              </li>
              <li><span className="link__num">3</span> Scan the QR code again to link to your account</li>
            </ol>

            <a className="link__help" href="/help">
              Need help?
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M7 17 17 7m0 0H9m8 0v8" stroke="currentColor" strokeWidth="2"
                  strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
          </div>

          <div className="link__qr-side">
            <div className={`link__qr link__qr--${state}`}>
              <canvas ref={canvasRef} className="link__qr-canvas" aria-label="Pairing QR code" />

              {state === 'ready' && (
                <span className="link__qr-mark" aria-hidden="true">
                  <svg viewBox="0 0 48 48" fill="none">
                    <circle cx="24" cy="24" r="22" fill="var(--ziv-primary)" />
                    <path d="M17 18h14l-14 13h14" stroke="#fff" strokeWidth="3.4"
                      strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              )}

              {state === 'loading' && <span className="link__spinner" aria-label="Loading" />}

              {state === 'expired' && (
                <button type="button" className="link__refresh" onClick={draw}>
                  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M20 12a8 8 0 1 1-2.3-5.6M20 4v4h-4" stroke="currentColor"
                      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Click to reload
                </button>
              )}
            </div>

            <Link className="link__phone" to="/login">
              Log in with phone number
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="m9 6 6 6-6 6" stroke="currentColor" strokeWidth="2"
                  strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </Link>
          </div>
        </section>

        <footer className="link__foot">
          <p className="link__signup">
            Don’t have a ZIvapp account? <Link to="/onboarding">Get started</Link>
          </p>

          <p className="link__e2e">
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <rect x="4" y="10" width="16" height="11" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
              <path d="M8 10V7.5a4 4 0 0 1 8 0V10" stroke="currentColor" strokeWidth="1.8"
                strokeLinecap="round" />
            </svg>
            {/* Not "end-to-end encrypted" — see the note in
                NoChatSelected.jsx. ZIvapp has no end-to-end encryption
                yet, and a false padlock is worse than no padlock. */}
            Your messages are encrypted in transit
          </p>

          <p className="link__terms">
            <a href="/terms">Terms &amp; Privacy Policy</a>
          </p>
        </footer>
      </div>
    </main>
  )
}
